import React, { useEffect, useRef, useState } from 'react';

interface CommittedNumberFieldOptions {
  /** The saved value: shown when not being edited, and reverted to on invalid input. */
  value: number;
  /** Parses the draft. Return `null` to reject it, which reverts the field to `value`. */
  parse: (draft: string) => number | null;
  /** A rejected promise means the save did not land, and the field shows `value` again. */
  onCommit: (value: number) => Promise<unknown> | void;
}

/**
 * Edits a numeric field as a local draft and commits on blur or Enter.
 *
 * Saving on every keystroke persisted intermediate values: editing `1` to `3` wrote `13` on the
 * way through, and a `min` attribute prevented clearing the field to retype. Holding a draft also
 * stops the input from remounting mid-edit, which used to steal focus after each character.
 */
export const useCommittedNumberField = ({ value, parse, onCommit }: CommittedNumberFieldOptions) => {
  const [draft, setDraft] = useState(String(value));

  // The draft already sent. Enter commits, and the blur that follows the same gesture commits
  // again before the saved value comes back — two saves for one edit, the second of them refused
  // as built from a superseded copy. Typing resets it, so a rejected value can be sent again.
  const sent = useRef<string | null>(null);

  // Re-sync when the saved value changes underneath us (reload, or a sibling field's save).
  useEffect(() => { setDraft(String(value)); }, [value]);

  const commit = () => {
    const parsed = parse(draft);
    if (parsed === null) {
      setDraft(String(value));
      return;
    }
    if (parsed === value || draft === sent.current) return;
    sent.current = draft;
    // Nothing was stored, so the draft must not stay on screen as if it had been. Waiting for the
    // saved value to come back does not clear it: a refused save leaves that value where it was.
    Promise.resolve(onCommit(parsed)).catch(() => setDraft(String(value)));
  };

  return {
    value: draft,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      sent.current = null;
      setDraft(e.target.value);
    },
    onBlur: commit,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      commit();
    },
  };
};

export default useCommittedNumberField;
